// src/pages/DashboardPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { inventoryAPI, alertsAPI } from '../services/api';
import { StatCard, Card, Badge, SectionHeader } from '../components/ui';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    LineChart, Line, CartesianGrid
} from 'recharts';
import { format, fromUnixTime } from 'date-fns';
import {
    Boxes, TrendingDown, AlertTriangle, ArrowDownLeft,
    ArrowUpRight, Package, RefreshCw
} from 'lucide-react';
import styles from './DashboardPage.module.css';

export default function DashboardPage() {
    const { user, companyId, isGaafAdmin } = useAuth();
    const navigate = useNavigate();

    const [stats, setStats] = useState(null);
    const [inventory, setInventory] = useState([]);
    const [events, setEvents] = useState([]);
    const [lowStock, setLowStock] = useState([]);
    const [loading, setLoading] = useState(true);

    // For GAAF admin: use their selected company or the first available one
    // For regular users: always use their companyId from the JWT
    const effectiveCompanyId = companyId && companyId !== 'gaafrfid_platform' ? companyId : null;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Always send companyId if we have a real one; admin without one sees all data
            const params = effectiveCompanyId ? { companyId: effectiveCompanyId } : {};
            const [statsRes, invRes, eventsRes, lowRes] = await Promise.all([
                inventoryAPI.getStats(params),
                inventoryAPI.get(params),
                inventoryAPI.getEvents({ ...params, limit: 50, from: Math.floor(Date.now() / 1000) - 90 * 86400, to: Math.floor(Date.now() / 1000) + 60 * 86400 }),
                inventoryAPI.getLowStock(params),
            ]);
            setStats(statsRes.data.stats);
            setInventory(invRes.data.inventory || []);
            setEvents(eventsRes.data.events || []);
            setLowStock(lowRes.data.lowStockProducts || []);
        } catch (_) { }
        setLoading(false);
    }, [effectiveCompanyId, isGaafAdmin]);

    useEffect(() => { load(); }, [load]);

    // Build daily chart data from events
    const chartData = buildChartData(events);

    const StatSkeleton = () => (
        <div className={styles.statSkeleton}>
            <div className="skeleton" style={{ height: 14, width: '60%', marginBottom: 12 }} />
            <div className="skeleton" style={{ height: 36, width: '40%', marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 12, width: '80%' }} />
        </div>
    );

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title={`${greeting()}, ${firstName(user)}`}
                description="Live Inventory Overview"
                actions={
                    <button className={styles.refreshBtn} onClick={load} disabled={loading}>
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                }
            />

            {/* ── Stats row ── */}
            <div className={`${styles.statsGrid} stagger`}>
                {loading ? (
                    [1, 2, 3, 4].map(i => <StatSkeleton key={i} />)
                ) : (
                    <>
                        <StatCard
                            label="Total Tags"
                            value={stats?.totalTags?.toLocaleString() ?? 0}
                            sub="RFID tags registered"
                            icon={<Boxes size={18} />}
                            className="animate-fadeIn"
                        />
                        <StatCard
                            label="In Stock"
                            value={stats?.inStock?.toLocaleString() ?? 0}
                            sub="Items in warehouse"
                            icon={<Package size={18} />}
                            accent
                            className="animate-fadeIn"
                        />
                        <StatCard
                            label="Today Entered"
                            value={stats?.todayEntered ?? 0}
                            sub="Received today"
                            icon={<ArrowDownLeft size={18} />}
                            className="animate-fadeIn"
                        />
                        <StatCard
                            label="Today Departed"
                            value={stats?.todayDeparted ?? 0}
                            sub="Shipped today"
                            icon={<ArrowUpRight size={18} />}
                            className="animate-fadeIn"
                        />
                    </>
                )}
            </div>

            {/* ── Charts + feed row ── */}
            <div className={styles.midRow}>
                {/* Movement chart */}
                <Card className={styles.chartCard}>
                    <h3 className={styles.cardTitle}>Movement History</h3>
                    {loading ? (
                        <div className="skeleton" style={{ height: 200, marginTop: 16 }} />
                    ) : chartData.length === 0 ? (
                        <div className={styles.chartEmpty}>No Movement Data yet</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={210}>
                            <BarChart data={chartData} barGap={4}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(74,158,110,0.08)" />
                                <XAxis dataKey="day" tick={{ fill: '#5a6b54', fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: '#5a6b54', fontSize: 11 }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{
                                        background: 'var(--bg-elevated)',
                                        border: '1px solid var(--border-default)',
                                        borderRadius: 8,
                                        color: 'var(--text-primary)',
                                        fontSize: 12,
                                    }}
                                />
                                <Bar dataKey="entered" name="Entered" fill="#4a9e6e" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="departed" name="Departed" fill="#2a6042" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Card>

                {/* Inventory by product */}
                <Card className={styles.donutCard}>
                    <h3 className={styles.cardTitle}>By Product</h3>
                    <div className={styles.productList}>
                        {loading ? (
                            [1, 2, 3].map(i => (
                                <div key={i} className={styles.productRow}>
                                    <div className="skeleton" style={{ height: 14, width: '50%' }} />
                                    <div className="skeleton" style={{ height: 14, width: '20%' }} />
                                </div>
                            ))
                        ) : inventory.length === 0 ? (
                            <p className={styles.emptyText}>No Inventory Data</p>
                        ) : (
                            inventory.slice(0, 8).map((item) => (
                                <div
                                    key={item.productId}
                                    className={styles.productRow}
                                    onClick={() => navigate('/inventory')}
                                >
                                    <div className={styles.productName}>
                                        <span className={styles.productDot} />
                                        {item.productName}
                                    </div>
                                    <div className={styles.productCount}>
                                        <span className={styles.productNum}>{item.inStock}</span>
                                        <span className={styles.productUnit}> in stock</span>
                                        {item.isLowStock && <Badge variant="warning" size="sm">Low</Badge>}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </Card>
            </div>

            {/* ── Bottom row: events + low stock ── */}
            <div className={styles.bottomRow}>
                {/* Live event feed */}
                <Card className={styles.feedCard} padding={false}>
                    <div className={styles.feedHeader}>
                        <h3 className={styles.cardTitle}>Live Movement Feed</h3>
                        <span className={styles.livePip} />
                    </div>
                    <div className={styles.feedList}>
                        {loading ? (
                            [1, 2, 3, 4, 5].map(i => (
                                <div key={i} className={styles.feedItem}>
                                    <div className="skeleton" style={{ height: 12, width: '70%' }} />
                                    <div className="skeleton" style={{ height: 10, width: '30%', marginTop: 4 }} />
                                </div>
                            ))
                        ) : events.length === 0 ? (
                            <div className={styles.feedEmpty}>No Movements yet. Send Tag Reads to see them here.</div>
                        ) : (
                            events.map((ev) => (
                                <div key={ev.eventId} className={styles.feedItem}>
                                    <div className={styles.feedLeft}>
                                        <span className={`${styles.feedDot} ${ev.eventType === 'ENTERED' ? styles.dotGreen : ev.eventType === 'DEPARTED' ? styles.dotOrange : styles.dotGray}`} />
                                        <div>
                                            <span className={styles.feedProduct}>{ev.productName || ev.itemId?.slice(-8)}</span>
                                            <span className={styles.feedAction}> {eventLabel(ev.eventType)}</span>
                                        </div>
                                    </div>
                                    <span className={styles.feedTime}>
                                        {ev.timestamp ? format(fromUnixTime(ev.timestamp), 'MM/dd HH:mm') : '—'}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </Card>

                {/* Low stock alerts */}
                <Card className={styles.alertCard} padding={false}>
                    <div className={styles.feedHeader}>
                        <h3 className={styles.cardTitle}>Low Stock Alerts</h3>
                        {lowStock.length > 0 && (
                            <Badge variant="warning">{lowStock.length}</Badge>
                        )}
                    </div>
                    <div className={styles.feedList}>
                        {loading ? (
                            [1, 2, 3].map(i => (
                                <div key={i} className={styles.feedItem}>
                                    <div className="skeleton" style={{ height: 14, width: '60%' }} />
                                </div>
                            ))
                        ) : lowStock.length === 0 ? (
                            <div className={styles.feedEmpty}>
                                <span style={{ color: 'var(--green-400)' }}>✓</span> All Products are adequately stocked
                            </div>
                        ) : (
                            lowStock.map((item, i) => (
                                <div key={i} className={styles.alertItem}>
                                    <AlertTriangle size={14} className={styles.alertIcon} />
                                    <div className={styles.alertInfo}>
                                        <span className={styles.alertProduct}>{item.product.name}</span>
                                        <span className={styles.alertDetail}>{item.summary} · threshold {item.threshold}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildChartData(events) {
    if (!events || events.length === 0) return [];

    // Build chart from actual event dates — no hardcoded 7-day window
    // This handles events with any timestamp (past or future test data)
    const map = {};

    events.forEach((ev) => {
        if (!ev.timestamp) return;
        const key = format(fromUnixTime(ev.timestamp), 'MM/dd');
        if (!map[key]) map[key] = { day: key, entered: 0, departed: 0, ts: ev.timestamp };
        if (ev.eventType === 'ENTERED') map[key].entered++;
        if (ev.eventType === 'DEPARTED') map[key].departed++;
    });

    // Sort by timestamp and return last 7 unique days with data
    const sorted = Object.values(map).sort((a, b) => a.ts - b.ts);

    // Also fill in the last 7 real calendar days (zero-filled) so chart always shows something
    const calendarDays = {};
    for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const key = format(d, 'MM/dd');
        if (!map[key]) calendarDays[key] = { day: key, entered: 0, departed: 0, ts: d.getTime() / 1000 };
    }

    // Merge: real event days + calendar days, sort by ts, take last 10
    const merged = Object.values({ ...calendarDays, ...map }).sort((a, b) => a.ts - b.ts);
    return merged.slice(-10);
}

function eventLabel(type) {
    switch (type) {
        case 'ENTERED': return 'entered warehouse';
        case 'DEPARTED': return 'left warehouse';
        case 'DETECTED': return 'detected';
        default: return type?.toLowerCase();
    }
}

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 18) return 'Good Afternoon';
    return 'Good Evening';
}

function firstName(user) {
    if (!user) return '';
    const name = user.name || user.email || '';
    return name.split(/[\s@]/)[0];
}
